import {
  userErrorMessage,
  foxKifuAiStatusResponseSchema,
  foxKifuAiDefaultResponseSchema,
  type FoxKifuAiStatusResponse,
  createManagedAccountResponseSchema,
  foxKifuAiAccountAccessListResponseSchema,
  foxKifuAiAccountAccessResponseSchema,
  foxKifuAiAccountListResponseSchema,
  foxKifuAiAccountResponseSchema,
  foxKifuAiEngineCatalogResponseSchema,
  organizationMemberResponseSchema,
  organizationMembersResponseSchema,
  organizationResponseSchema,
  platformOrganizationsResponseSchema,
  type ManagedOrganizationMember,
  type FoxKifuAiComputeSelection,
  type FoxKifuAiAccount,
  type FoxKifuAiAccountListResponse,
  type FoxKifuAiUserAccess,
  type OrganizationSummary,
  type PlatformOrganization,
} from '@wx-weiqi/contracts';

import { nextRequestId } from '../services/http';
import type { IdentityStore } from './identity-store';

export interface AdminState {
  readonly aiSelection?: FoxKifuAiStatusResponse['data'] | null;
  readonly organizations: readonly PlatformOrganization[];
  readonly selectedOrganizationId: string | null;
  readonly organization: OrganizationSummary | null;
  readonly members: readonly ManagedOrganizationMember[];
  readonly membersNextAfter: string | null;
  readonly memberFilters: AdminMemberFilters;
  readonly aiManagement: FoxKifuAiAccountListResponse['data'] | null;
  readonly aiAccessByAccount: Readonly<Record<string, readonly FoxKifuAiUserAccess[]>>;
  readonly pending: string | null;
  readonly error: string | null;
}

export interface AdminMemberFilters {
  readonly search?: string;
  readonly role?: 'STUDENT' | 'TEACHER' | 'ADMIN';
  readonly status?: 'ACTIVE' | 'INACTIVE';
}

type Listener = () => void;

export class AdminStore {
  private stateValue: AdminState = {
    organizations: [],
    selectedOrganizationId: null,
    organization: null,
    members: [],
    membersNextAfter: null,
    memberFilters: {},
    aiManagement: null,
    aiAccessByAccount: {},
    pending: null,
    error: null,
  };
  private readonly listeners = new Set<Listener>();

  constructor(
    private readonly identity: IdentityStore,
    private readonly currentOrganizationId: () => string | null,
  ) {}

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): AdminState => this.stateValue;

  async loadZhiziEngineCatalog(configurationId: string, refresh = false) {
    const response = await this.identity.authorized(
      `/platform/fox-kifu-ai/accounts/${encodeURIComponent(configurationId)}/engine-catalog${refresh ? '?refresh=true' : ''}`,
      foxKifuAiEngineCatalogResponseSchema,
    );
    return response.data;
  }

  async load(): Promise<void> {
    if (this.isPlatformAdmin()) {
      await this.loadPlatformOrganizations();
      return;
    }
    const organizationId = this.requireIdentityOrganizationId();
    await this.loadOrganization(organizationId);
  }

  async loadPlatformOrganizations(search = ''): Promise<void> {
    this.update({ ...this.stateValue, pending: 'load', error: null });
    try {
      const response = await this.identity.authorized(
        `/platform/organizations${search.trim() === '' ? '' : `?search=${encodeURIComponent(search.trim())}`}`,
        platformOrganizationsResponseSchema,
      );
      const selectedOrganizationId =
        response.data.organizations.find(
          (organization) => organization.organizationId === this.stateValue.selectedOrganizationId,
        )?.organizationId ??
        response.data.organizations[0]?.organizationId ??
        null;
      this.update({
        ...this.stateValue,
        organizations: response.data.organizations,
        selectedOrganizationId,
        pending: null,
        error: null,
      });
      if (selectedOrganizationId !== null) await this.loadOrganization(selectedOrganizationId);
      else {
        this.update({
          ...this.stateValue,
          organization: null,
          members: [],
          membersNextAfter: null,
          memberFilters: {},
          pending: null,
          error: null,
        });
      }
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async selectOrganization(organizationId: string): Promise<void> {
    this.update({ ...this.stateValue, selectedOrganizationId: organizationId });
    await this.loadOrganization(organizationId);
  }

  async loadMembers(filters: AdminMemberFilters = {}): Promise<void> {
    const organizationId = this.requireManagedOrganizationId();
    this.update({ ...this.stateValue, pending: 'load-members', error: null });
    try {
      const parameters = new URLSearchParams({ limit: '100' });
      if (filters.search?.trim()) parameters.set('search', filters.search.trim());
      if (filters.role !== undefined) parameters.set('role', filters.role);
      if (filters.status !== undefined) parameters.set('status', filters.status);
      const response = await this.identity.authorized(
        `/organizations/${encodeURIComponent(organizationId)}/accounts?${parameters.toString()}`,
        organizationMembersResponseSchema,
      );
      this.update({
        ...this.stateValue,
        members: response.data.members,
        membersNextAfter: response.data.nextAfter,
        memberFilters: filters,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async loadMoreMembers(): Promise<void> {
    const after = this.stateValue.membersNextAfter;
    if (after === null) return;
    const organizationId = this.requireManagedOrganizationId();
    this.update({ ...this.stateValue, pending: 'load-more-members', error: null });
    try {
      const parameters = new URLSearchParams({ limit: '100', after });
      const filters = this.stateValue.memberFilters;
      if (filters.search?.trim()) parameters.set('search', filters.search.trim());
      if (filters.role !== undefined) parameters.set('role', filters.role);
      if (filters.status !== undefined) parameters.set('status', filters.status);
      const response = await this.identity.authorized(
        `/organizations/${encodeURIComponent(organizationId)}/accounts?${parameters.toString()}`,
        organizationMembersResponseSchema,
      );
      this.update({
        ...this.stateValue,
        members: [...this.stateValue.members, ...response.data.members],
        membersNextAfter: response.data.nextAfter,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  private async loadOrganization(organizationId: string): Promise<void> {
    this.update({ ...this.stateValue, pending: 'load-organization', error: null });
    try {
      const [organization, members] = await Promise.all([
        this.identity.authorized(
          `/organizations/${encodeURIComponent(organizationId)}`,
          organizationResponseSchema,
        ),
        this.identity.authorized(
          `/organizations/${encodeURIComponent(organizationId)}/accounts?limit=100`,
          organizationMembersResponseSchema,
        ),
      ]);
      this.update({
        ...this.stateValue,
        selectedOrganizationId: organizationId,
        organization: organization.data,
        members: members.data.members,
        membersNextAfter: members.data.nextAfter,
        memberFilters: {},
        aiManagement: null,
        aiAccessByAccount: {},
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async loadAiManagement(): Promise<void> {
    this.pending('zhizi-management-load');
    try {
      const response = await this.identity.authorized(
        '/platform/fox-kifu-ai/accounts',
        foxKifuAiAccountListResponseSchema,
      );
      const selection = await this.identity.authorized(
        '/fox-kifu/ai/status',
        foxKifuAiStatusResponseSchema,
      );
      this.update({
        ...this.stateValue,
        aiSelection: selection.data,
        aiManagement: response.data,
        aiAccessByAccount: {},
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async saveAiDefault(configurationId: string): Promise<void> {
    this.pending('zhizi-default-save');
    try {
      await this.identity.authorized(
        '/platform/fox-kifu-ai/my-default',
        foxKifuAiDefaultResponseSchema,
        {
          method: 'PUT',
          requestId: nextRequestId('teacher-ai-default'),
          body: { configurationId, version: this.stateValue.aiSelection?.defaultVersion ?? 0 },
        },
      );
      await this.loadAiManagement();
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async addZhiziAccount(account: string, password: string): Promise<FoxKifuAiAccount> {
    this.pending('zhizi-account-add');
    try {
      const response = await this.identity.authorized(
        '/platform/fox-kifu-ai/accounts',
        foxKifuAiAccountResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-zhizi-account-add'),
          body: { account: account.trim(), password },
        },
      );
      const management = this.stateValue.aiManagement;
      if (management !== null) {
        this.update({
          ...this.stateValue,
          aiManagement: { ...management, accounts: [...management.accounts, response.data] },
          pending: null,
          error: null,
        });
      }
      return response.data;
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async configureZhiziAccount(
    account: FoxKifuAiAccount,
    compute: FoxKifuAiComputeSelection,
    accountLabel?: string,
  ): Promise<void> {
    this.pending(`zhizi-configuration-${account.configurationId}`);
    try {
      const response = await this.identity.authorized(
        `/platform/fox-kifu-ai/accounts/${encodeURIComponent(account.configurationId)}/profile`,
        foxKifuAiAccountResponseSchema,
        {
          method: 'PUT',
          requestId: nextRequestId('web-zhizi-configure'),
          body: {
            version: account.version,
            compute,
            ...(accountLabel === undefined ? {} : { accountLabel: accountLabel.trim() }),
          },
        },
      );
      this.update({
        ...this.stateValue,
        aiManagement:
          this.stateValue.aiManagement === null
            ? null
            : {
                ...this.stateValue.aiManagement,
                accounts: this.stateValue.aiManagement.accounts.map((candidate) =>
                  candidate.configurationId === response.data.configurationId
                    ? response.data
                    : candidate,
                ),
              },
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async loadAiAccess(configurationId: string): Promise<void> {
    const organizationId = this.requireManagedOrganizationId();
    this.pending(`zhizi-access-load-${configurationId}`);
    try {
      const response = await this.identity.authorized(
        `/platform/fox-kifu-ai/accounts/${encodeURIComponent(configurationId)}/organizations/${encodeURIComponent(organizationId)}/access`,
        foxKifuAiAccountAccessListResponseSchema,
      );
      this.update({
        ...this.stateValue,
        aiAccessByAccount: {
          ...this.stateValue.aiAccessByAccount,
          [configurationId]: response.data.users,
        },
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async updateAiAccess(
    configurationId: string,
    access: FoxKifuAiUserAccess,
    authorized: boolean,
  ): Promise<void> {
    const organizationId = this.requireManagedOrganizationId();
    this.pending(`zhizi-access-${access.userId}`);
    try {
      const response = await this.identity.authorized(
        `/platform/fox-kifu-ai/accounts/${encodeURIComponent(configurationId)}/organizations/${encodeURIComponent(organizationId)}/access/${encodeURIComponent(access.userId)}`,
        foxKifuAiAccountAccessResponseSchema,
        {
          method: 'PATCH',
          requestId: nextRequestId('web-zhizi-access'),
          body: { version: access.version, authorized },
        },
      );
      this.update({
        ...this.stateValue,
        aiAccessByAccount: Object.fromEntries(
          Object.entries(this.stateValue.aiAccessByAccount).map(([accountId, accesses]) => [
            accountId,
            accesses.map((candidate) =>
              candidate.userId !== response.data.access.userId
                ? candidate
                : accountId === configurationId
                  ? response.data.access
                  : { ...response.data.access, authorized: false },
            ),
          ]),
        ),
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async createOrganization(name: string, slug: string): Promise<void> {
    this.pending('create-organization');
    try {
      const response = await this.identity.authorized(
        '/platform/organizations',
        organizationResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-organization-create'),
          body: { name: name.trim(), slug: slug.trim().toLowerCase() },
        },
      );
      await this.loadPlatformOrganizations();
      await this.selectOrganization(response.data.organizationId);
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async renameOrganization(name: string): Promise<void> {
    const organizationId = this.requireManagedOrganizationId();
    const organization = this.stateValue.organization;
    if (organization === null) throw new Error('请先加载棋院信息。');
    this.pending('rename-organization');
    try {
      const response = await this.identity.authorized(
        `/organizations/${encodeURIComponent(organizationId)}`,
        organizationResponseSchema,
        {
          method: 'PATCH',
          requestId: nextRequestId('web-organization-rename'),
          body: { version: organization.version, name: name.trim() },
        },
      );
      this.update({ ...this.stateValue, organization: response.data, pending: null, error: null });
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async createAccount(input: {
    readonly username: string;
    readonly password: string;
    readonly displayName: string;
    readonly role: 'STUDENT' | 'TEACHER';
  }): Promise<void> {
    const organizationId = this.requireManagedOrganizationId();
    this.pending('create-account');
    try {
      const response = await this.identity.authorized(
        `/organizations/${encodeURIComponent(organizationId)}/accounts`,
        createManagedAccountResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-account-create'),
          body: {
            username: input.username.trim(),
            password: input.password,
            displayName: input.displayName.trim(),
            role: input.role,
          },
        },
      );
      this.update({
        ...this.stateValue,
        members: [...this.stateValue.members, response.data.member],
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async updateMember(
    member: ManagedOrganizationMember,
    change: {
      readonly role?: 'STUDENT' | 'TEACHER' | 'ADMIN';
      readonly status?: 'ACTIVE' | 'INACTIVE';
    },
  ): Promise<void> {
    const organizationId = this.requireManagedOrganizationId();
    this.pending(`member-${member.membershipId}`);
    try {
      const response = await this.identity.authorized(
        `/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(member.membershipId)}`,
        organizationMemberResponseSchema,
        {
          method: 'PATCH',
          requestId: nextRequestId('web-member-update'),
          body: { version: member.version, ...change },
        },
      );
      this.update({
        ...this.stateValue,
        members: this.stateValue.members.map((candidate) =>
          candidate.membershipId === response.data.membershipId
            ? { ...candidate, ...response.data }
            : candidate,
        ),
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async unlockAccount(member: ManagedOrganizationMember): Promise<void> {
    if (this.stateValue.pending !== null) throw new Error('请等待当前操作完成。');
    const organizationId = this.requireManagedOrganizationId();
    this.pending(`unlock-${member.membershipId}`);
    try {
      const response = await this.identity.authorized(
        `/organizations/${encodeURIComponent(organizationId)}/accounts/${encodeURIComponent(member.membershipId)}/unlock`,
        organizationMemberResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-account-unlock'),
          body: { profileVersion: member.user.profileVersion },
        },
      );
      if (this.stateValue.selectedOrganizationId !== organizationId) return;
      this.update({
        ...this.stateValue,
        members: this.stateValue.members.map((candidate) =>
          candidate.membershipId === member.membershipId
            ? { ...candidate, ...response.data }
            : candidate,
        ),
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      if (this.stateValue.selectedOrganizationId === organizationId) this.fail(error);
      throw error;
    }
  }

  clear(): void {
    this.update({
      organizations: [],
      selectedOrganizationId: null,
      organization: null,
      members: [],
      membersNextAfter: null,
      memberFilters: {},
      aiManagement: null,
      aiAccessByAccount: {},
      pending: null,
      error: null,
    });
  }

  private requireIdentityOrganizationId(): string {
    const organizationId = this.currentOrganizationId();
    if (organizationId === null) throw new Error('请先选择棋院身份。');
    return organizationId;
  }

  private requireManagedOrganizationId(): string {
    const organizationId =
      this.stateValue.selectedOrganizationId ?? this.requireIdentityOrganizationId();
    return organizationId;
  }

  private isPlatformAdmin(): boolean {
    return this.identity.snapshot().session?.data.user.isPlatformAdmin === true;
  }

  private pending(operation: string): void {
    this.update({ ...this.stateValue, pending: operation, error: null });
  }

  private fail(error: unknown): void {
    this.update({
      ...this.stateValue,
      pending: null,
      error: userErrorMessage(error, '管理员操作失败，请重试。'),
    });
  }

  private update(state: AdminState): void {
    this.stateValue = state;
    this.listeners.forEach((listener) => listener());
  }
}
